<template>
  <section id="batch-plan-table">
    <b-table
      :data="items"
      :mobile-cards="false"
      narrowed
      sticky-header
      :row-class="rowClass"
      :height="height"
    >
      <b-table-column label="" width="40" centered v-slot="props">
        <b-tooltip v-show="props.row.error" position="is-right">
          <b-icon icon="information" type="is-danger"></b-icon>
          <template v-slot:content>
            {{ props.row.error }}
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.group_code')"
        width="100"
        centered
        sortable
        v-slot="props"
      >
        <b-tooltip type="is-light" position="is-right">
          <p>{{ props.row.code }}</p>
          <template v-slot:content>
            <p v-html="props.row.name"></p>
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="departure_date"
        :label="$t('attributes.departure_date')"
        width="140"
        sortable
        centered
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ formatDate(props.row.departure_date) }}
      </b-table-column>

      <b-table-column :label="$t('attributes.plan')" centered v-slot="props">
        <b-table
          :data="props.row.plans"
          :mobile-cards="false"
          narrowed
          hoverable
          :class="`plan-table ` + rowClass(props.row, props.index)"
          :row-class="() => rowClass(props.row, props.index)"
        >
          <b-table-column
            field="sort"
            width="80"
            numeric
            :header-class="rowClass(props.row, props.index)"
            v-slot="plans"
          >
            <b-field
              :label="plans.index === 0 ? $t('attributes.sort') : ''"
              label-position="on-border"
            >
              <b-select
                v-model="plans.row.sort"
                name="sort"
                @input="modified(props.row)"
              >
                <option
                  v-for="option in sort_range"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </b-field>
          </b-table-column>

          <b-table-column
            field="is_main"
            width="90"
            centered
            :header-class="rowClass(props.row, props.index)"
            v-slot="plans"
          >
            <b-field
              :label="plans.index === 0 ? $t('attributes.main_plan') : ''"
              label-position="on-border"
            >
              <b-radio
                v-model="props.row.main_plan_id"
                :name="`main-plan-radio-${props.row.code}`"
                :native-value="plans.row.id"
                style="margin-top: 0.5rem; margin-right: 0;"
                @input="planId => onSelectMainPlan(props.row, planId)"
              >
              </b-radio>
            </b-field>
          </b-table-column>

          <b-table-column
            field="name"
            width="180"
            :header-class="rowClass(props.row, props.index)"
            v-slot="plans"
          >
            <b-field
              :label="
                plans.index === 0
                  ? $t('attributes.plan') + $t('attributes.name')
                  : ''
              "
              label-position="on-border"
            >
              <b-input
                :ref="`name-input-${props.index}-${plans.index}`"
                v-model="plans.row.name"
                :placeholder="$t('attributes.plan') + $t('attributes.name')"
                @input="modified(props.row)"
                @keypress.enter.exact.native.prevent="
                  finish(props.index, plans.index, 'name', 1)
                "
                @keypress.enter.shift.exact.native.prevent="
                  finish(props.index, plans.index, 'name', -1)
                "
              ></b-input>
            </b-field>
          </b-table-column>

          <b-table-column
            v-for="count in props.row.traveler_type_count"
            :key="props.row.traveler_types[count - 1].id"
            :header-class="rowClass(props.row, props.index)"
            v-slot="plans"
          >
            <b-field
              :label="
                plans.index === 0
                  ? props.row.traveler_types[count - 1].traveler_type
                      .display_name
                  : ''
              "
              label-position="on-border"
            >
              <b-input
                :ref="`price-input-${props.index}-${plans.index}-${count - 1}`"
                v-model="plans.row.traveler_types[count - 1].priceValue"
                type="text"
                custom-class="align-right"
                :placeholder="$t('attributes.price')"
                @input="
                  modifiedPrice(props.row, plans.row.traveler_types[count - 1])
                "
                @keypress.enter.exact.native.prevent="
                  enterPrice(props.index, plans.index, count - 1, 1)
                "
                @keypress.enter.shift.exact.native.prevent="
                  enterPrice(props.index, plans.index, count - 1, -1)
                "
                @blur="
                  finishPrice(props.row, plans.row.traveler_types[count - 1])
                "
              ></b-input>
            </b-field>
          </b-table-column>
        </b-table>
      </b-table-column>

      <b-table-column
        width="50"
        :label="$t('attributes.function')"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-tooltip :label="$t('attributes.restore')">
            <b-button
              type="is-info"
              icon-left="restore"
              outlined
              @click.prevent="restore(props.row)"
            >
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section" style="height: 100%">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>

<script>
export default {
  name: 'BatchPlanTable',

  props: {
    groups: {
      Type: Array,
      required: true
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      sort_range: [...Array(20).keys()].map(i => i + 1),
      items: this.extractGroups(this.groups)
    }
  },
  watch: {
    groups(list) {
      this.items = this.extractGroups(list)
    }
  },
  methods: {
    extractGroups(groups) {
      return groups.map(group => {
        const plans = group.plans.sort((t1, t2) => t1.sort - t2.sort)
        const main_plan = group.plans.find(t => t.is_main)
        const main_plan_id = main_plan && main_plan.id
        const traveler_types =
          group.plans.length > 0
            ? group.plans[0].traveler_types.sort((t1, t2) => t1.sort - t2.sort)
            : []

        return {
          code: group.code,
          departure_date: group.departure_date,
          name: group.name,
          main_plan_id: main_plan_id,
          plans: plans.map(plan => {
            return {
              ...plan,
              traveler_types: plan.traveler_types
                .sort((t1, t2) => t1.sort - t2.sort)
                .map(t => ({ ...t, priceValue: this.formatCurrency(t.price) }))
            }
          }),
          traveler_types: traveler_types,
          traveler_type_count: traveler_types.length,

          editing: false,
          dirty: false,
          error: null,
          origin: {
            ...group,
            main_plan_id: main_plan_id,
            plans: plans.map(plan => {
              return {
                ...plan,
                traveler_types: plan.traveler_types
                  .sort((t1, t2) => t1.sort - t2.sort)
                  .map(t => ({
                    ...t,
                    priceValue: this.formatCurrency(t.price)
                  }))
              }
            })
          }
        }
      })
    },
    onSelectMainPlan(group, planId) {
      group.plans = group.plans.map(p => ({
        ...p,
        is_main: p.id === planId
      }))

      this.modified(group)
    },
    modifiedPrice(group, traveler_type) {
      traveler_type.price = this.parsePrice(traveler_type.priceValue)
      this.modified(group)
    },
    finishPrice(group, traveler_type) {
      traveler_type.price = this.parsePrice(traveler_type.priceValue)
      traveler_type.priceValue = this.formatCurrency(traveler_type.price)
      this.modified(group)

      this.$nextTick(
        () =>
          (traveler_type.priceValue = this.formatCurrency(traveler_type.price))
      )
    },
    parsePrice(value) {
      let priceValue = value || '0'
      if (priceValue.startsWith('-')) {
        priceValue = '0'
      }

      let price = priceValue.replaceAll(',', '')
      price = parseInt(price)
      if (isNaN(price)) {
        price = 0
      }

      return price
    },
    enterPrice(groupIndex, planIndex, current, dir) {
      const next = current + dir
      const group = this.items[groupIndex]
      const plan = group.plans[planIndex]

      let nextGroupIdx = groupIndex
      let nextPlanIdx = planIndex
      let index = next

      if (next < 0) {
        nextPlanIdx -= 1
        if (nextPlanIdx < 0) {
          nextGroupIdx -= 1
          if (nextGroupIdx < 0) {
            nextGroupIdx = this.items.length - 1
          }
          const nextGroup = this.items[nextGroupIdx]
          nextPlanIdx = nextGroup.plans.length - 1
          const nextPlan = nextGroup.plans[nextPlanIdx]
          index = nextPlan.traveler_types.length - 1
        } else {
          const nextPlan = group.plans[nextPlanIdx]
          index = nextPlan.traveler_types.length - 1
        }
      } else if (next >= plan.traveler_types.length) {
        nextPlanIdx += 1
        if (nextPlanIdx >= group.plans.length) {
          nextGroupIdx += 1
          nextPlanIdx = 0
          if (nextGroupIdx >= this.items.length) {
            nextGroupIdx = 0
          }
          index = 0
        } else {
          nextPlanIdx = planIndex + 1
          index = 0
        }
      }

      setTimeout(() => {
        const name = `price-input-${nextGroupIdx}-${nextPlanIdx}-${index}`
        if (this.$refs[name] && this.$refs[name].length > 0) {
          const input = this.$refs[name][0].getElement()
          if (input) {
            input.focus()
          }
        }
      }, 0)
    },
    finish(groupIdx, planIdx, field, dir) {
      const group = this.items[groupIdx]

      this.modified(group)

      let nextGroupIdx = groupIdx
      let nextPlanIdx = planIdx + dir

      if (nextPlanIdx >= group.plans.length) {
        nextGroupIdx += 1
        nextPlanIdx = 0
        if (nextGroupIdx >= this.items.length) {
          nextGroupIdx = 0
        }
      } else if (nextPlanIdx < 0) {
        nextGroupIdx -= 1
        if (nextGroupIdx < 0) {
          nextGroupIdx = this.items.length - 1
        }
        nextPlanIdx = this.items[nextGroupIdx].plans.length - 1
      }

      setTimeout(() => {
        const name = `${field}-input-${nextGroupIdx}-${nextPlanIdx}`
        const input = this.$refs[name].getElement()
        if (input) {
          input.focus()
        }
      }, 0)
    },
    modified(group) {
      group.dirty =
        group.main_plan_id !== group.origin.main_plan_id ||
        JSON.stringify(group.plans) !== JSON.stringify(group.origin.plans)

      this.validate(group)
    },
    restore(group) {
      group.main_plan_id = group.origin.main_plan_id
      group.plans = group.origin.plans.map(plan => {
        return {
          ...plan,
          traveler_types: plan.traveler_types
            .sort((t1, t2) => t1.sort - t2.sort)
            .map(t => ({ ...t }))
        }
      })

      group.dirty = false
      group.error = false
    },
    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return this.items.filter(x => x.editing).length > 0
    },
    isValidate() {
      for (const group of this.items) {
        if (!this.validate(group)) {
          return false
        }
      }

      return true
    },
    validate(group) {
      for (const plan of group.plans) {
        if (!plan.name) {
          group.error = `方案名稱必填`
          return false
        }

        for (const traveler of plan.traveler_types) {
          if (traveler.price < 0) {
            group.error = `${traveler.traveler_type.display_name}不可小於0`
            return false
          }
        }
      }

      group.error = null
      return true
    },
    getData() {
      return this.items
        .filter(group => group.dirty)
        .map(group => ({
          code: group.code,
          plans: group.plans.map(plan => {
            return {
              ...plan,
              traveler_types: plan.traveler_types.map(traveler => ({
                id: traveler.id,
                price: traveler.price
              }))
            }
          })
        }))
    },
    rowClass(group, index) {
      if (group.error) {
        return 'has-background-danger-light'
      } else if (group.dirty) {
        return 'has-background-warning'
      } else {
        return index % 2 === 1 ? 'has-background-info-light' : ''
      }
    }
  }
}
</script>

<style lang="scss">
#batch-plan-table {
  .b-table {
    .table-wrapper.has-sticky-header tr:first-child th {
      z-index: 10;
    }

    .table {
      min-width: 1000px;

      .align-right {
        text-align: right;
      }

      .plan-table {
        .table-wrapper tr:first-child th {
          z-index: 2;
        }

        .field.is-floating-label .label:before {
          background-color: transparent;
        }

        .table {
          border: none;

          td,
          th {
            border: none;
          }
        }
      }
    }
  }
}
</style>
